import { fr } from "@codegouvfr/react-dsfr"
import { Card } from "@codegouvfr/react-dsfr/Card"
import { Box, Typography } from "@mui/material"
import type { IGetRoutes, IResponse } from "shared"
import { JOB_START_TYPE } from "shared"
import type { LBA_ITEM_TYPE } from "shared/constants/lbaitem"
import { newItemTypeToOldItemType } from "shared/constants/lbaitem"

import { CardStyling } from "@/app/(candidat)/(recherche)/recherche/_components/RechercheResultats/CardStyling"
import ItemDetailApplicationsStatus from "@/components/ItemDetail/ItemDetailServices/ItemDetailApplicationStatus"
import { LbaJobEngagementTag } from "@/components/ItemDetail/LbaJobComponents/LbaJobEngagementTag"
import { TagCandidatureSpontanee } from "@/components/ItemDetail/TagCandidatureSpontanee"
import { TagEmploiFormation } from "@/components/ItemDetail/TagEmploiFormation"
import { TagFormation } from "@/components/ItemDetail/TagFormation"
import { TagOffreEmploi } from "@/components/ItemDetail/TagOffreEmploi"
import { TagRecrutementUrgent } from "@/components/ItemDetail/TagRecrutementUrgent"
import { getDaysSinceDate } from "@/utils/days-since-date"
import type { ILbaItem } from "@/utils/lba-item.types"
import { getMatomoJobOfferType, MATOMO_EVENTS, pushMatomoEvent, SEARCH_ENGINES } from "@/utils/matomo-utils"

import type { ISearchPageParams } from "../_utils/search.params.utils"
import { buildHitDetailUrl, buildSearchUrl } from "../_utils/search.params.utils"
import { activeFilterNames, sortTrackingValueOf } from "../_utils/search.tracking.utils"

type SearchResponse = IResponse<IGetRoutes["/v1/search"]>
export type Hit = SearchResponse["hits"][number]

interface SearchHitCardProps {
  hit: Hit
  currentParams: ISearchPageParams
  /** Position 1-based dans la liste de résultats (télémétrie). */
  position: number
  /** Callback ref sur le conteneur de la carte — utilisé par SearchResultsList pour y rescroller au retour d'une fiche détail. */
  cardRef?: (node: HTMLElement | null) => void
  /** Halo temporaire signalant la carte consultée au retour d'une fiche détail (cf. SearchResultsList). */
  isHighlighted?: boolean
}

const isAlgoCompany = (hit: Hit) => hit.is_algo_company === true
const isFormation = (hit: Hit) => hit.type === "formation"

// Mêmes badges que la carte legacy (composants feuilles réutilisés), pilotés par les
// champs des documents de recherche au lieu de l'ideaType legacy.
function HitTags({ hit }: { hit: Hit }) {
  const tags: React.ReactNode[] = []

  if (isAlgoCompany(hit)) tags.push(<TagCandidatureSpontanee key="candidature-spontanee" />)
  else if (isFormation(hit)) tags.push(<TagFormation key="formation" />)
  else if (hit.is_formation_included) tags.push(<TagEmploiFormation key="emploi-formation" />)
  else tags.push(<TagOffreEmploi key="offre-emploi" />)

  if (hit.is_disabled_elligible) tags.push(<LbaJobEngagementTag key="handi" />)
  if (hit.start_type === JOB_START_TYPE.DES_QUE_POSSIBLE) tags.push(<TagRecrutementUrgent key="urgent" />)

  return <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>{tags}</Box>
}

function CompanyLine({ hit }: { hit: Hit }) {
  // Candidature spontanée : le titre est déjà le nom d'entreprise → on affiche le secteur.
  if (isAlgoCompany(hit)) return <>Secteur d'activité : {hit.activity_sector ?? ""}</>
  return hit.organization_name ? (
    <>{hit.organization_name}</>
  ) : (
    <Box component="span" sx={{ fontStyle: "italic" }}>
      Offre anonyme
    </Box>
  )
}

function DatePublication({ hit }: { hit: Hit }) {
  // Comme en legacy : offres uniquement — la publication_date des candidatures spontanées
  // est une date d'import, celle des formations est nulle.
  if (isFormation(hit) || isAlgoCompany(hit) || !hit.publication_date) return null
  const daysPublished = getDaysSinceDate(new Date(hit.publication_date))

  return (
    <Typography component="span" sx={{ color: fr.colors.decisions.text.mention.grey.default, py: fr.spacing("1v") }} className={fr.cx("fr-text--xs")}>
      Publiée {daysPublished ? `depuis ${daysPublished} jour(s)` : "aujourd'hui"}
    </Typography>
  )
}

function CandidatureCount({ hit }: { hit: Hit }) {
  if (!hit.smart_apply || hit.application_count == null) return null

  return (
    <Typography
      component="span"
      sx={{ whiteSpace: "nowrap", color: fr.colors.decisions.text.default.info.default, py: fr.spacing("1v") }}
      className={fr.cx("fr-text--xs", "fr-text--bold", "fr-icon-flashlight-fill", "fr-icon--sm")}
    >
      {`${hit.application_count} CANDIDATURE${hit.application_count > 1 ? "S" : ""}`}
    </Typography>
  )
}

export function SearchHitCard({ hit, currentParams, position, cardRef, isHighlighted }: SearchHitCardProps) {
  const currentSearchUrl = buildSearchUrl(currentParams)
  const detailUrl = buildHitDetailUrl({ sub_type: hit.sub_type ?? "", url_id: hit.url_id ?? "", title: hit.title ?? "" }, currentSearchUrl)

  // Même événement que la carte legacy (formations exclues, comme en legacy), enrichi de la
  // dimension search_engine pour comparer les parcours des deux moteurs.
  const trackClick = isFormation(hit)
    ? undefined
    : () => {
        pushMatomoEvent({
          event: MATOMO_EVENTS.JOB_OFFER_CLICKED,
          job_offer_id: hit.url_id,
          job_offer_type: getMatomoJobOfferType(hit.sub_type as LBA_ITEM_TYPE),
          job_offer_company: hit.organization_name || "non_renseigné",
          job_offer_name: hit.title || "non_renseigné",
          position_in_list: position,
          has_contact: hit.smart_apply === true,
          search_job_name: currentParams.q || "non_renseigné",
          search_address: currentParams.lieu_label || "non_renseigné",
          // Contexte de conversion : filtres actifs (vocabulaire de search_filter_applied)
          // et tri — permet de relier filtres/tri aux consultations puis candidatures.
          active_filters: activeFilterNames(currentParams).join(",") || "none",
          active_filters_count: activeFilterNames(currentParams).length,
          sort_value: sortTrackingValueOf(currentParams.sort),
          search_engine: SEARCH_ENGINES.BETA,
        })
      }

  // Encart « déjà postulé » legacy réutilisé tel quel : il ne consomme que ideaType, id et
  // contact.hasEmail — reconstruits depuis le hit (mêmes clés localStorage que le legacy →
  // historique de candidatures partagé entre les deux moteurs).
  const applicationStatusItem = {
    ideaType: newItemTypeToOldItemType(hit.sub_type as LBA_ITEM_TYPE),
    id: hit.url_id,
    contact: { hasEmail: hit.smart_apply === true },
  } as unknown as ILbaItem

  // Même composition que LbaItemCard (legacy) : wrapper zIndex + CardStyling (padding de
  // contenu 16px) + Card DSFR — hauteur et padding identiques aux cartes de /recherche.
  return (
    <Box
      ref={cardRef}
      sx={{
        my: fr.spacing("2v"),
        borderRadius: "8px",
        // Halo de la carte consultée au retour d'une fiche détail (cf. SearchResultsList) :
        // box-shadow pour ne pas déplacer les cartes voisines. Le reset du state côté liste
        // (≥ la durée) permet de rejouer l'animation ; `both` fige l'état final transparent,
        // sans saut quand `animation` repasse à none.
        "@keyframes shadow-pulse-highlight": {
          "0%": { boxShadow: "0 0 0 0 transparent" },
          "50%": { boxShadow: `0 0 10px 0 color-mix(in srgb, ${fr.colors.decisions.border.plain.blueEcume.default} 55%, transparent)` },
          "100%": { boxShadow: "0 0 0 0 transparent" },
        },
        animation: isHighlighted ? "shadow-pulse-highlight .5s ease-in-out both" : "none",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          boxShadow: isHighlighted ? `0 0 10px 0 color-mix(in srgb, ${fr.colors.decisions.border.plain.blueEcume.default} 55%, transparent)` : "none",
        },
        ".fr-card__title a::before": { zIndex: "unset" },
      }}
    >
      <CardStyling>
        <Card
          background
          style={{ paddingBottom: fr.spacing("1v") }}
          shadow
          enlargeLink
          horizontal
          linkProps={{ href: detailUrl, onClick: trackClick }}
          start={<HitTags hit={hit} />}
          title={
            <Typography component="span" className={fr.cx("fr-text--bold", "fr-text--md")} sx={{ color: fr.colors.decisions.text.actionHigh.grey.default }}>
              {hit.title ?? ""}
            </Typography>
          }
          desc={
            <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: fr.spacing("3v") }}>
              <Typography component="span" className={fr.cx("fr-text--sm")} color={fr.colors.decisions.text.actionHigh.grey.default}>
                <CompanyLine hit={hit} />
              </Typography>
              <Typography component="span" sx={{ color: fr.colors.decisions.text.title.grey.default }} className={fr.cx("fr-text--xs")}>
                {hit.address}
                {hit.distance != null && (
                  <>
                    <br />
                    <Typography component="span" sx={{ my: 0, fontWeight: 400, color: fr.colors.decisions.text.mention.grey.default }} className={fr.cx("fr-text--xs")}>
                      {hit.distance} km(s) du lieu de recherche
                    </Typography>
                  </>
                )}
              </Typography>

              <Box
                component="span"
                sx={{
                  alignItems: { xs: "left", sm: "left", md: "center" },
                  gap: { xs: fr.spacing("2v"), md: fr.spacing("1v") },
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <DatePublication hit={hit} />
                <CandidatureCount hit={hit} />
                <ItemDetailApplicationsStatus item={applicationStatusItem} />
              </Box>
            </Box>
          }
          size="medium"
        />
      </CardStyling>
    </Box>
  )
}
